import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface PainterRow {
  readonly painterUserId: string;
  readonly painterCode: string;
  readonly displayName: string;
  readonly mobileNumber: string;
  readonly active: boolean;
  readonly territoryId: string;
  readonly workTypes: readonly string[];
  readonly activeAssignmentCount: number;
}

export function PainterListPage(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly PainterRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    apiRequest<{ items: readonly PainterRow[] }>(`/v1/vendor/painters${search ? `?q=${encodeURIComponent(search)}` : ''}`, { token })
      .then((r) => { if (alive) setRows(r.items); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, search]);

  if (error) return <main aria-label="painter-list"><p role="alert">{error}</p></main>;
  if (!rows) return <main aria-label="painter-list"><p>Loading…</p></main>;

  return (
    <main aria-label="painter-list">
      <h1>Painters</h1>
      <label htmlFor="painter-search">Search</label>
      <input id="painter-search" aria-label="painter-search" value={search} onChange={(e) => setSearch(e.target.value)} />
      <Link to="/vendor/portal/painters/new" aria-label="painter-new-link">+ New painter</Link>

      {rows.length === 0 ? (
        <p aria-label="empty">No painters</p>
      ) : (
        <table aria-label="painter-table">
          <thead><tr><th>Code</th><th>Name</th><th>Mobile</th><th>Territory</th><th>Active jobs</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.painterUserId} aria-label={`row-${p.painterUserId}`}>
                <td>{p.painterCode}</td>
                <td>{p.displayName}</td>
                <td>{p.mobileNumber}</td>
                <td>{p.territoryId}</td>
                <td>{p.activeAssignmentCount}</td>
                <td>{p.active ? 'ACTIVE' : 'INACTIVE'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
